import React, { useState, useEffect } from 'react';
import { GoodThing } from '../../types/good-things';
import { Card, CardContent } from '../ui/card';
import { FavoriteButton } from './FavoriteButton';
import { ReactionButton } from './ReactionButton';
import { ReactionsList } from './ReactionsList';
import { Calendar, User, Users, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../hooks/useAuth';
import { employeeNamesService } from '../../services/employeeNames';
import { EditGoodThingDialog } from './EditGoodThingDialog';
import { goodThingsService, clearAllCaches } from '../../services/goodThings';
import { toast } from '../ui/use-toast';
import { convertPlainTextToRichText, convertRichTextToHTML } from '../../utils/richTextUtils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface GoodThingCardProps {
  goodThing: GoodThing;
  onUnfavorite?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const GoodThingCard: React.FC<GoodThingCardProps> = ({ 
  goodThing, 
  onUnfavorite,
  onEdit,
  onDelete
}) => {
  const { userEmail } = useAuth();
  const [isSubmitter, setIsSubmitter] = useState(false);
  const [isWithinEditWindow, setIsWithinEditWindow] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const checkIfSubmitter = async () => {
      if (!userEmail) return;
      
      const userName = await employeeNamesService.getNameFromEmail(userEmail);
      if (!userName) return;
      
      // Create name variations to handle different formats
      const nameVariations = [userName];
      if (userName.endsWith('.')) {
        nameVariations.push(userName.slice(0, -1));
      } else {
        nameVariations.push(`${userName}.`);
      }
      
      setIsSubmitter(nameVariations.includes(goodThing.submitter));
    };
    
    // Check if within edit window (today only)
    const checkEditWindow = () => {
      try {
        // Try to parse the date in different formats
        let creationDate: Date;
        
        // If the date includes time information (has a 'T' or ':')
        if (goodThing.date.includes('T') || goodThing.date.includes(':')) {
          creationDate = new Date(goodThing.date);
        } else {
          // Handle MM/DD/YY format
          if (goodThing.date.includes('/')) {
            const [month, day, year] = goodThing.date.split('/');
            // Add leading zeros if needed
            const paddedMonth = month.padStart(2, '0');
            const paddedDay = day.padStart(2, '0');
            // Assume 20xx for two-digit years
            const fullYear = year.length === 2 ? `20${year}` : year;
            
            // Create date string in ISO format (YYYY-MM-DD)
            const dateStr = `${fullYear}-${paddedMonth}-${paddedDay}`;
            
            creationDate = new Date(dateStr);
          } else {
            // Default to ISO format
            creationDate = new Date(goodThing.date);
          }
        }
        
        // If the date is invalid, default to not allowing edits
        if (isNaN(creationDate.getTime())) {
          console.warn('Invalid date format:', goodThing.date);
          setIsWithinEditWindow(false);
          return;
        }
        
        // Check if the Good Thing was created today (using EST timezone)
        // Get current date in EST timezone
        const today = new Date();
        
        // Convert dates to EST timezone for comparison
        const estOptions = { timeZone: 'America/New_York' };
        
        // Format today's date as MM/DD/YYYY in EST
        const todayFormatted = today.toLocaleDateString('en-US', estOptions);
        
        // Format creation date as MM/DD/YYYY in EST
        const creationDateFormatted = creationDate.toLocaleDateString('en-US', estOptions);
        
        // Get yesterday's date in EST timezone
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayFormatted = yesterday.toLocaleDateString('en-US', estOptions);
        
        // Allow editing if the Good Thing was created today OR yesterday
        const isWithinWindow = (todayFormatted === creationDateFormatted) || 
                              (yesterdayFormatted === creationDateFormatted);
        
        // Set the edit window state
        setIsWithinEditWindow(isWithinWindow);
      } catch (error) {
        console.error('Error parsing date:', error);
        // Default to not allowing edits if there's an error
        setIsWithinEditWindow(false);
      }
    };
    
    checkIfSubmitter();
    checkEditWindow();
  }, [userEmail, goodThing.submitter, goodThing.date]);

  const handleDelete = async () => {
    // First show a toast notification that the deletion is in progress
    toast({
      title: "Deleting Good Thing...",
      description: "Your Good Thing is being deleted. Please wait.",
      variant: "default"
    });
    
    try {
      // Close the dialog immediately
      setDeleteDialogOpen(false);
      
      // Delete the Good Thing
      await goodThingsService.deleteGoodThing(goodThing);
      
      // Clear caches to ensure fresh data
      clearAllCaches();
      
      // Add a delay to allow time for the backend to process the deletion
      // and for Firebase to sync the changes
      setTimeout(() => {
        // Show success toast without refresh button since sync works automatically
        toast({
          title: "Good Thing Deleted! 🗑️",
          description: "Your Good Thing has been successfully deleted.",
          variant: "success",
          duration: 5000, // 5 seconds
        });
      }, 5000); // 5 second delay
      
      // Call the onDelete callback if provided
      if (onDelete) onDelete();
    } catch (error) {
      console.error('Error deleting Good Thing:', error);
      toast({
        title: "Error",
        description: "Failed to delete the Good Thing. Please try again.",
        variant: "destructive"
      });
    }
  };

  const formatName = (email: string): string => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const formatRecipients = (recipients: string[]): string => {
    if (recipients.length === 0) return '';
    if (recipients.length === 1) return formatName(recipients[0]);
    
    // Format all recipients with commas and "and" before the last one
    const formattedNames = recipients.map(recipient => formatName(recipient));
formattedNames.sort(); // Sort names alphabetically by first name
    
    if (recipients.length === 2) {
      return `${formattedNames[0]} and ${formattedNames[1]}`;
    }
    
    // For 3 or more recipients, use commas and "and" before the last one
    const lastRecipient = formattedNames.pop();
    return `${formattedNames.join(', ')} and ${lastRecipient}`;
  };

  return (
    <>
      <Card className="h-full">
        <CardContent className="p-4 h-full flex flex-col">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              {/* Category */}
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold">
                  {goodThing.category}
                </h3>
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center">
                          <FavoriteButton goodThing={goodThing} onUnfavorite={onUnfavorite} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Add to Favorites</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <ReactionButton goodThingId={goodThing.id} className="h-10 w-10" />
                  
                  {isSubmitter && isWithinEditWindow && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditDialogOpen(true)}
                        className="h-8 w-8 rounded-full"
                        title="Edit Good Thing"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteDialogOpen(true)}
                        className="h-8 w-8 rounded-full text-red-500 hover:text-red-700 hover:bg-red-100"
                        title="Delete Good Thing"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Submitter and Recipients */}
              <div className="flex flex-col space-y-1 mb-3 text-sm mt-4">
                {/* Date - Moved above From/To */}
                <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-2">
                  <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  {(() => {
                    let displayDate: Date;
                    
                    // Handle MM/DD/YY format specifically to avoid timezone issues
                    if (goodThing.date.includes('/')) {
                      const [month, day, year] = goodThing.date.split('/');
                      
                      if (year) {
                        const paddedMonth = month.padStart(2, '0');
                        const paddedDay = day.padStart(2, '0');
                        const fullYear = year.length === 2 ? `20${year}` : year;
                        
                        // Create date in local timezone to avoid UTC conversion issues
                        displayDate = new Date(parseInt(fullYear), parseInt(paddedMonth) - 1, parseInt(paddedDay));
                      } else {
                        displayDate = new Date(goodThing.date);
                      }
                    } else {
                      // For ISO format dates (YYYY-MM-DD), parse as local date to avoid timezone issues
                      if (goodThing.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        const [year, month, day] = goodThing.date.split('-');
                        displayDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      } else {
                        // For other formats, use default Date constructor
                        displayDate = new Date(goodThing.date);
                      }
                    }
                    
                    return displayDate.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    });
                  })()}
                </div>
                {goodThing.recipients && goodThing.recipients.length > 0 && (
                  <div className="flex items-center">
                    <span className="font-medium text-blue-600 dark:text-blue-400 mr-1">To:</span>
                    <Users className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-1" />
                    <span className="text-gray-800 dark:text-gray-200">{formatRecipients(goodThing.recipients)}</span>
                  </div>
                )}
                
                <div className="flex items-center">
                  <span className="font-medium text-green-600 dark:text-green-400 mr-1">From:</span>
                  <User className="h-4 w-4 text-green-600 dark:text-green-400 mr-1" />
                  <span className="text-gray-800 dark:text-gray-200">{formatName(goodThing.submitter)}</span>
                </div>
              </div>

              {/* Good Thing Note */}
              <div className="text-gray-700 dark:text-gray-300">
                {goodThing.richNote ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: convertRichTextToHTML(goodThing.richNote)
                    }}
                  />
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: convertRichTextToHTML(convertPlainTextToRichText(goodThing.note))
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Reactions Row */}
          <div className="mt-2">
            <div className="flex justify-end">
              <ReactionsList goodThingId={goodThing.id} />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Edit Dialog */}
      {editDialogOpen && (
        <EditGoodThingDialog
          goodThing={goodThing}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSuccess={() => {
            if (onEdit) onEdit();
          }}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the Good Thing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
